import { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import { WorkHoursRow, WorkHoursRecordedRef, WorkHoursInvalidCell } from './work-hours-recorded.component';
import { getCertificateData, submitCertificate, getCertificateById, getDraftCertificateById, updateCertificate, updateDraftCertificate, getEmployeeById, saveDraftCertificate } from '../../features/employer-certificate/employer-certificate.slice';
import { OrderHeader } from './order-header.component';
import { ImportantInfoBox } from './important-info-box.component';
import { ContractDetails } from './contract-details.component';
import { EmploymentDetails } from './employment-details.component';
import { WorkHoursRecorded } from './work-hours-recorded.component';
import { DiscontinuationDetails } from './discontinuation-details.component';
import { SalaryInformation } from './salary-information.component';
import { OtherInformation } from './other-information.component';
import { OrderFormFooter } from './order-form-footer.component';
import './employer-certificate-order-form.component.scss';

interface EmployerCertificateOrderFormProps {
    onCancel?: () => void;
    onSubmitSuccess?: () => void;
    editCertificateId?: number;
    isEmployerCertificateDraft?: boolean;
}

export const EmployerCertificateOrderForm: React.FC<EmployerCertificateOrderFormProps> = ({
    onCancel,
    onSubmitSuccess,
    editCertificateId,
    isEmployerCertificateDraft = false,
}) => {
    const submitQueryParams = new URLSearchParams(window.location.search);
    const isEmployerCertificateDraftFromQuery = submitQueryParams.get('isEmployerCertificateDraft') === 'true';
    const certificateIdFromQuery = Number(submitQueryParams.get('certificateId') || 0);

    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [isCurrentlyWorking, setIsCurrentlyWorking] = useState(false);
    const [reasonType, setReasonType] = useState('');
    const [reasonDescription, setReasonDescription] = useState('');
    const [year, setYear] = useState('');
    const [salaryType, setSalaryType] = useState('');
    const [amount, setAmount] = useState('');
    const [otherInfo, setOtherInfo] = useState('');
    const [confirmAccurate, setConfirmAccurate] = useState(false);

    const [employmentErrors, setEmploymentErrors] = useState<{ startDate?: string; endDate?: string }>({});
    const [workHoursInvalidCells, setWorkHoursInvalidCells] = useState<WorkHoursInvalidCell[]>([]);
    const [salaryErrors, setSalaryErrors] = useState<{ year?: string; amount?: string }>({});
    const [otherInfoError, setOtherInfoError] = useState('');
    const workHoursRef = useRef<WorkHoursRecordedRef>(null);
    const { t } = useTranslation();

    const dispatch = useDispatch<any>();
    const certificateData = useSelector((state: any) => state.employerCertificate.certificateData);
    const isCertificateLoading = useSelector((state: any) => state.employerCertificate.loading);
    const isSubmitting = useSelector((state: any) => state.employerCertificate.submitting);
    const isSavingDraft = useSelector((state: any) => state.employerCertificate.savingDraft);
    const isUpdating = useSelector((state: any) => state.employerCertificate.updating);
    const isUpdatingDraft = useSelector((state: any) => state.employerCertificate.updatingDraft);
    const selectedCertificate = useSelector((state: any) => state.employerCertificate.selectedCertificate);
    const selectedCertificateLoading = useSelector((state: any) => state.employerCertificate.selectedCertificateLoading);
    const employeeData = useSelector((state: any) => state.employerCertificate.employeeData);
    const [isWorkHoursLoading, setIsWorkHoursLoading] = useState(false);

    const user = useSelector((state: any) => state.auth.user);

    useEffect(() => {
        if (user?.userId) {
            dispatch(getEmployeeById(user.userId));
        }
    }, [user?.userId]);

    useEffect(() => {
        if (editCertificateId) {
            if (isEmployerCertificateDraft) {
                dispatch(getDraftCertificateById(editCertificateId));
            } else {
                dispatch(getCertificateById(editCertificateId));
            }
        }
    }, [editCertificateId, isEmployerCertificateDraft]);

    useEffect(() => {
        if (editCertificateId) return; // use employeeWagesDetails from selectedCertificate when editing
        const effectiveEndDate = isCurrentlyWorking ? new Date().toISOString().split('T')[0] : endDate;
        if (startDate && effectiveEndDate) {
            setIsWorkHoursLoading(true);
            dispatch(getCertificateData({ fromDate: startDate, toDate: effectiveEndDate }))
                .finally(() => setIsWorkHoursLoading(false));
        }
    }, [startDate, endDate, isCurrentlyWorking]);

    const KNOWN_REASON_TYPES = [
        'contract-ended', 'resignation', 'termination', 'mutual-agreement',
        'end-of-assignment', 'retirement', 'redundancy', 'other',
        // Swedish labels stored by API
        'Kontraktet avslutades', 'Uppsägning av anställd', 'Uppsägning av arbetsgivare',
        'Ömsesidig överenskommelse', 'Uppdragets slut', 'Pension',
        'Övertalighet / Avskedning', 'Annan orsak',
    ];

    useEffect(() => {
        if (selectedCertificate) {
            if (selectedCertificate.employmentPeriodFrom) setStartDate(selectedCertificate.employmentPeriodFrom.slice(0, 10));
            const stillEmployed = selectedCertificate.isStillEmployed ?? false;
            setIsCurrentlyWorking(stillEmployed);
            if (!stillEmployed && selectedCertificate.employmentPeriodEnd) {
                setEndDate(selectedCertificate.employmentPeriodEnd.slice(0, 10));
            } else {
                setEndDate('');
            }

            if (selectedCertificate.otherReason) {
                const val = selectedCertificate.otherReason;
                if (val === 'other' || val === 'Annan orsak') {
                    // "Other reason" was selected with no custom text
                    setReasonType('other');
                } else if (KNOWN_REASON_TYPES.includes(val)) {
                    // Known Swedish label (e.g. 'Ömsesidig överenskommelse') — use as-is
                    setReasonType(val);
                } else {
                    // Custom free-text — show "Other reason" + populate description
                    setReasonType('other');
                    setReasonDescription(val);
                }
            }

            if (selectedCertificate.salaryRefers) {
                const yr = new Date(selectedCertificate.salaryRefers).getFullYear();
                if (!isNaN(yr)) setYear(String(yr));
            }

            if (selectedCertificate.amountForOverTime != null) {
                setAmount(String(selectedCertificate.amountForOverTime));
            }

            if (selectedCertificate.otherInfomationEmployment) {
                setOtherInfo(selectedCertificate.otherInfomationEmployment);
            }
        }
    }, [selectedCertificate]);

    const workHoursRows: WorkHoursRow[] = useMemo(() => {
        if (editCertificateId && selectedCertificate?.employeeWagesDetails?.length) {
            return selectedCertificate.employeeWagesDetails.map((item: any) => ({
                monthYear: item.month,
                avgWorkDuration: item.hoursworked,
                absence: item.absence ?? 0,
                overTime: item.overtime ?? 0,
                totalAmount: item.totalAmount,
                totalWorkingDays: item.totalWorkingDays ?? 0,
            }));
        }
        return certificateData.map((item: any) => ({
            monthYear: item.monthYear,
            avgWorkDuration: item.totalHours,
            absence: 0,
            overTime: 0,
            totalAmount: item.totalAmount,
            totalWorkingDays: item.totalWorkingDays ?? 0,
        }));
    }, [certificateData, editCertificateId, selectedCertificate]);

    // Contract details from employee API (falls back to selectedCertificate for edit mode)
    const contractData = {
        contractNo: employeeData?.phoneNo ?? selectedCertificate?.phoneNo ?? '',
        empName: employeeData
            ? `${employeeData.firstName} ${employeeData.lastName}`.trim()
            : selectedCertificate
            ? `${selectedCertificate.firstName} ${selectedCertificate.lastName}`.trim()
            : '',
        socialSecurityNumber: employeeData?.ssn
            ?? selectedCertificate?.socialSecurityNumber
            ?? '',
    };

    const handleCancel = () => {
        if (onCancel) {
            onCancel();
        } else {
            // Reset form as fallback
            console.log('Cancel clicked - no onCancel handler provided');
        }
    };

    const handleUpdate = () => {
        let hasErrors = false;
        const newEmploymentErrors: { startDate?: string; endDate?: string } = {};
        const newSalaryErrors: { year?: string; amount?: string } = {};

        if (!startDate) {
            newEmploymentErrors.startDate = 'Start date is required';
            hasErrors = true;
        }
        if (!isCurrentlyWorking && !endDate) {
            newEmploymentErrors.endDate = 'End date is required';
            hasErrors = true;
        }
        setEmploymentErrors(newEmploymentErrors);

        const invalidCells = workHoursRef.current?.getInvalidCells() ?? [];
        setWorkHoursInvalidCells(invalidCells);
        if (invalidCells.length > 0) {
            workHoursRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            hasErrors = true;
        }

        if (!year) {
            newSalaryErrors.year = 'Year is required';
            hasErrors = true;
        }
        if (!amount.trim()) {
            newSalaryErrors.amount = 'Amount is required';
            hasErrors = true;
        }
        setSalaryErrors(newSalaryErrors);

        if (hasErrors) return;

        const rows = workHoursRef.current?.getRows() ?? [];
        const nameParts = contractData.empName.split(' ');

        const parseMonthYear = (monthYear: string) => {
            const d = new Date(monthYear);
            if (!isNaN(d.getTime())) {
                const month = d.toLocaleString('en-US', { month: 'short' });
                return { month, hoursDate: `${d.getFullYear()}-${month}-01` };
            }
            return { month: monthYear, hoursDate: monthYear };
        };

        dispatch(updateCertificate({
            ...selectedCertificate,
            certificateId: editCertificateId,
            firstName: nameParts[0] ?? '',
            lastName: nameParts.slice(1).join(' '),
            socialSecurityNumber: contractData.socialSecurityNumber,
            employmentPeriodFrom: startDate,
            employmentPeriodEnd: isCurrentlyWorking ? new Date().toISOString().split('T')[0] : endDate,
            isStillEmployed: isCurrentlyWorking,
            reportedTimeFromDate: startDate,
            reportedTimeEndDate: isCurrentlyWorking ? new Date().toISOString().split('T')[0] : endDate,
            salaryRefers: year ? `${year}-01-01` : '',
            amountForOverTime: parseFloat(amount) || 0,
            otherInfomationEmployment: otherInfo,
            otherReason: reasonType === 'other' ? reasonDescription : reasonType,
            isOtherReason: reasonType !== '',
            isHourlySalary: true,
            employeeWagesDetails: rows.map(row => {
                const { month, hoursDate } = parseMonthYear(row.monthYear);
                return {
                    id: 0,
                    certificateId: editCertificateId,
                    month,
                    hoursworked: row.avgWorkDuration,
                    absence: row.absence,
                    overtime: row.overTime,
                    mertidFillingTime: 0,
                    hoursDate,
                    userId: 0,
                    totalAmount: row.totalAmount,
                    totalWorkingDays: row.totalWorkingDays,
                };
            }),
        })).then((result: any) => {
            if (result.meta?.requestStatus === 'fulfilled') {
                const msg: string = String(t('employerCertificateUpdatedSuccessfully'));
                toast.success(msg);
                onSubmitSuccess?.();
            } else {
                const msg: string = String(t('somethingWentWrong'));
                toast.error(msg);
            }
        });
    };

    const handleSubmit = () => {
        let hasErrors = false;
        const newEmploymentErrors: { startDate?: string; endDate?: string } = {};
        const newSalaryErrors: { year?: string; amount?: string } = {};

        if (!startDate) {
            newEmploymentErrors.startDate = 'Start date is required';
            hasErrors = true;
        }
        if (!isCurrentlyWorking && !endDate) {
            newEmploymentErrors.endDate = 'End date is required';
            hasErrors = true;
        }
        setEmploymentErrors(newEmploymentErrors);

        const invalidCells = workHoursRef.current?.getInvalidCells() ?? [];
        setWorkHoursInvalidCells(invalidCells);
        if (invalidCells.length > 0) {
            workHoursRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            hasErrors = true;
        }

        if (!year) {
            newSalaryErrors.year = 'Year is required';
            hasErrors = true;
        }
        if (!amount.trim()) {
            newSalaryErrors.amount = 'Amount is required';
            hasErrors = true;
        }
        setSalaryErrors(newSalaryErrors);

        if (hasErrors) return;

        const rows = workHoursRef.current?.getRows() ?? [];
        const nameParts = contractData.empName.split(' ');
        const draftCertificateId = isEmployerCertificateDraftFromQuery
            ? (certificateIdFromQuery || editCertificateId || 0)
            : 0;

        const parseMonthYear = (monthYear: string) => {
            const d = new Date(monthYear);
            if (!isNaN(d.getTime())) {
                const month = d.toLocaleString('en-US', { month: 'short' });
                return {
                    month,
                    hoursDate: `${d.getFullYear()}-${month}-01`,
                    monthdate: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`,
                };
            }
            return { month: monthYear, hoursDate: monthYear, monthdate: monthYear };
        };

        dispatch(submitCertificate({
            ...(draftCertificateId > 0 ? { draftCertificateId } : {}),
            selectPeriod: true,
            individualInvoices: false,
            firstName: nameParts[0] ?? '',
            lastName: nameParts.slice(1).join(' '),
            socialSecurityNumber: contractData.socialSecurityNumber,
            employmentPeriodFrom: startDate,
            employmentPeriodEnd: isCurrentlyWorking ? new Date().toISOString().split('T')[0] : endDate,
            isStillEmployed: isCurrentlyWorking,
            employeePosition: '',
            reportedTimeFromDate: startDate,
            reportedTimeEndDate: isCurrentlyWorking ? new Date().toISOString().split('T')[0] : endDate,
            teachingHours: 0,
            salaryRefers: year ? `${year}-01-01` : '',
            amountForOverTime: parseFloat(amount) || 0,
            userName: contractData.empName,
            employerName: '',
            streetAddress: '',
            mailingAddress: '',
            organizationNumber: '',
            telephoneNumber: '',
            otherInfomationEmployment: otherInfo,
            otherReason: reasonType === 'other' ? reasonDescription : reasonType,
            isFullTimehoursPerWeek: false,
            isPartTimehoursPerWeek: false,
            isVaryingHours: false,
            employeeOwnRequest: false,
            isOtherReason: reasonType !== '',
            isContinueWorkFrom: false,
            isUntilFurtherNotice: false,
            isContinueVariableWorkingHours: false,
            isContinueEmployeeAccepted: false,
            isTimeWorkedUntilFurtherNotice: false,
            isTeachingHours: false,
            isMonthlySalary: false,
            isWeeklySalary: false,
            isDalilySalary: false,
            isHourlySalary: true,
            isFillingWorkvaried: false,
            isSalaryInAdditional: false,
            isEmployeeWithHoliday: false,
            isEmployeeWithLivingWage: false,
            isCrossInstedofTick: false,
            isTickInCorrectly: false,
            specialInfoEmployment: false,
            isCompensationTermination: false,
            EmployeeWagesDetails: rows.map(row => {
                const { month, hoursDate } = parseMonthYear(row.monthYear);
                return {
                    month,
                    hoursworked: row.avgWorkDuration,
                    absence: row.absence,
                    overtime: row.overTime,
                    totalAmount: row.totalAmount,
                    totalWorkingDays: row.totalWorkingDays,
                    hoursDate,
                };
            }),
            CertificateDetailsPerMonth: [],
        })).then((result: any) => {
            if (result.meta?.requestStatus === 'fulfilled') {
                const msg: string = String(t('employerCertificateCreatedSuccessfully'));
                toast.success(msg);
                onSubmitSuccess?.();
            } else {
                const msg: string = String(t('somethingWentWrong'));
                toast.error(msg);
            }
        });
    };

    const handleSaveDraft = () => {
        const rows = workHoursRef.current?.getRows() ?? [];
        const nameParts = contractData.empName.split(' ');

        const parseMonthYear = (monthYear: string) => {
            const d = new Date(monthYear);
            if (!isNaN(d.getTime())) {
                const month = d.toLocaleString('en-US', { month: 'short' });
                return {
                    month,
                    hoursDate: `${d.getFullYear()}-${month}-01`,
                    monthdate: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`,
                };
            }
            return { month: monthYear, hoursDate: monthYear, monthdate: monthYear };
        };

        dispatch(saveDraftCertificate({
            selectPeriod: true,
            individualInvoices: false,
            firstName: nameParts[0] ?? '',
            lastName: nameParts.slice(1).join(' '),
            socialSecurityNumber: contractData.socialSecurityNumber,
            employmentPeriodFrom: startDate || '',
            employmentPeriodEnd: isCurrentlyWorking ? new Date().toISOString().split('T')[0] : (endDate || ''),
            isStillEmployed: isCurrentlyWorking,
            employeePosition: '',
            reportedTimeFromDate: startDate || '',
            reportedTimeEndDate: isCurrentlyWorking ? new Date().toISOString().split('T')[0] : (endDate || ''),
            teachingHours: 0,
            salaryRefers: year ? `${year}-01-01` : '',
            amountForOverTime: parseFloat(amount) || 0,
            userName: contractData.empName,
            employerName: '',
            streetAddress: '',
            mailingAddress: '',
            organizationNumber: '',
            telephoneNumber: '',
            otherInfomationEmployment: otherInfo,
            otherReason: reasonType === 'other' ? reasonDescription : reasonType,
            isFullTimehoursPerWeek: false,
            isPartTimehoursPerWeek: false,
            isVaryingHours: false,
            employeeOwnRequest: false,
            isOtherReason: reasonType !== '',
            isContinueWorkFrom: false,
            isUntilFurtherNotice: false,
            isContinueVariableWorkingHours: false,
            isContinueEmployeeAccepted: false,
            isTimeWorkedUntilFurtherNotice: false,
            isTeachingHours: false,
            isMonthlySalary: false,
            isWeeklySalary: false,
            isDalilySalary: false,
            isHourlySalary: true,
            isFillingWorkvaried: false,
            isSalaryInAdditional: false,
            isEmployeeWithHoliday: false,
            isEmployeeWithLivingWage: false,
            isCrossInstedofTick: false,
            isTickInCorrectly: false,
            specialInfoEmployment: false,
            isCompensationTermination: false,
            EmployeeWagesDetails: rows.map(row => {
                const { month, hoursDate } = parseMonthYear(row.monthYear);
                return {
                    month,
                    hoursworked: row.avgWorkDuration,
                    absence: row.absence,
                    overtime: row.overTime,
                    totalAmount: row.totalAmount,
                    totalWorkingDays: row.totalWorkingDays,
                    hoursDate,
                };
            }),
            CertificateDetailsPerMonth: [],
            isDraft: true,
        })).then((result: any) => {
            if (result.meta?.requestStatus === 'fulfilled') {
                const msg: string = String(t('Draft saved successfully'));
                toast.success(msg);
                onSubmitSuccess?.();
            } else {
                const msg: string = String(t('Failed to save draft'));
                toast.error(msg);
            }
        });
    };

    const handleUpdateDraft = () => {
        const rows = workHoursRef.current?.getRows() ?? [];
        const nameParts = contractData.empName.split(' ');

        const parseMonthYear = (monthYear: string) => {
            const d = new Date(monthYear);
            if (!isNaN(d.getTime())) {
                const month = d.toLocaleString('en-US', { month: 'short' });
                return {
                    month,
                    hoursDate: `${d.getFullYear()}-${month}-01`,
                };
            }
            return { month: monthYear, hoursDate: monthYear };
        };

        dispatch(updateDraftCertificate({
            certificateId: editCertificateId!,
            payload: {
                ...selectedCertificate,
                certificateId: editCertificateId,
                firstName: nameParts[0] ?? '',
                lastName: nameParts.slice(1).join(' '),
                socialSecurityNumber: contractData.socialSecurityNumber,
                employmentPeriodFrom: startDate || '',
                employmentPeriodEnd: isCurrentlyWorking ? new Date().toISOString().split('T')[0] : (endDate || ''),
                isStillEmployed: isCurrentlyWorking,
                reportedTimeFromDate: startDate || '',
                reportedTimeEndDate: isCurrentlyWorking ? new Date().toISOString().split('T')[0] : (endDate || ''),
                salaryRefers: year ? `${year}-01-01` : '',
                amountForOverTime: parseFloat(amount) || 0,
                otherInfomationEmployment: otherInfo,
                otherReason: reasonType === 'other' ? reasonDescription : reasonType,
                isOtherReason: reasonType !== '',
                isHourlySalary: true,
                isDraft: true,
                employeeWagesDetails: rows.map(row => {
                    const { month, hoursDate } = parseMonthYear(row.monthYear);
                    return {
                        id: 0,
                        certificateId: editCertificateId,
                        month,
                        hoursworked: row.avgWorkDuration,
                        absence: row.absence,
                        overtime: row.overTime,
                        mertidFillingTime: 0,
                        hoursDate,
                        userId: 0,
                        totalAmount: row.totalAmount,
                        totalWorkingDays: row.totalWorkingDays,
                    };
                }),
            },
        })).then((result: any) => {
            if (result.meta?.requestStatus === 'fulfilled') {
                const msg: string = String(t('Draft updated successfully'));
                toast.success(msg);
                onSubmitSuccess?.();
            } else {
                const msg: string = String(t('Failed to update draft'));
                toast.error(msg);
            }
        });
    };

    return (
        <div className="employer-certificate-order-form">
            <OrderHeader />
            
            <ImportantInfoBox />
            
            <ContractDetails
                certificateId={selectedCertificate?.certificateId}
                empName={contractData.empName}
                socialSecurityNumber={contractData.socialSecurityNumber}
            />
            
            <EmploymentDetails
                startDate={startDate}
                endDate={endDate}
                isCurrentlyWorking={isCurrentlyWorking}
                onStartDateChange={setStartDate}
                onEndDateChange={setEndDate}
                onCurrentlyWorkingChange={setIsCurrentlyWorking}
                errors={employmentErrors}
            />
            
            <WorkHoursRecorded
                ref={workHoursRef}
                rows={workHoursRows}
                invalidCells={workHoursInvalidCells}
                isLoading={isWorkHoursLoading}
            />
            
                                    <DiscontinuationDetails
                                        reasonType={reasonType}
                                        reasonDescription={reasonDescription}
                                        isCurrentlyWorking={isCurrentlyWorking}
                                        onReasonTypeChange={setReasonType}
                                        onReasonDescriptionChange={setReasonDescription}
                                    />
            
            <SalaryInformation
                year={year}
                salaryType={salaryType}
                amount={amount}
                onYearChange={setYear}
                onSalaryTypeChange={setSalaryType}
                onAmountChange={setAmount}
                errors={salaryErrors}
            />
            
            <OtherInformation
                otherInfo={otherInfo}
                confirmAccurate={confirmAccurate}
                onOtherInfoChange={setOtherInfo}
                onConfirmAccurateChange={setConfirmAccurate}
                otherInfoError={otherInfoError}
            />
            
            <OrderFormFooter
                onCancel={handleCancel}
                onSubmit={handleSubmit}
                isEditMode={!!editCertificateId}
                isEmployerCertificateDraft={isEmployerCertificateDraft}
                onUpdate={handleUpdate}
                onSaveDraft={handleSaveDraft}
                onUpdateDraft={handleUpdateDraft}
                disabled={!confirmAccurate}
                isSavingDraft={isSavingDraft}
                isUpdatingDraft={isUpdatingDraft}
            />
        </div>
    );
};
